import { useCallback, useEffect, useMemo, useState } from 'react';
import { createAccountProvider } from '../../integrations/yogo-account/adapter';
import {
  CreditError,
  type AccountProvider,
  type AccountSession,
  type CreditBalance,
} from '../../integrations/yogo-account/types';

export interface AccountViewState {
  loading: boolean;
  authenticated: boolean;
  session: AccountSession | null;
  credits: CreditBalance | null;
  /** 账号已登录但积分服务不可用/未关联时的提示 */
  creditsError: string | null;
  error: string | null;
}

const INITIAL: AccountViewState = {
  loading: true,
  authenticated: false,
  session: null,
  credits: null,
  creditsError: null,
  error: null,
};

/**
 * 账号状态：登录态与积分都只**读取**账号能力。
 *
 * 登录是整页跳转到账号中心（`signIn()`），因此这里没有账号密码字段；
 * 扣费与免费额度都由服务端在转换开始前记账，前端只读展示、永远不参与余额计算。
 */
export function useAccount(provider?: AccountProvider) {
  const account = useMemo(() => provider ?? createAccountProvider(), [provider]);
  const [state, setState] = useState<AccountViewState>(INITIAL);

  const refresh = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const session = await account.getSession();
      if (!session.authenticated) {
        setState({ loading: false, authenticated: false, session, credits: null, creditsError: null, error: null });
        return;
      }

      let credits: CreditBalance | null = null;
      let creditsError: string | null = null;
      try {
        credits = await account.getCredits();
      } catch (error) {
        creditsError = error instanceof CreditError ? error.message : '积分服务暂不可用';
      }

      setState({ loading: false, authenticated: true, session, credits, creditsError, error: null });
    } catch (error) {
      setState({
        loading: false,
        authenticated: false,
        session: null,
        credits: null,
        creditsError: null,
        error: describe(error),
      });
    }
  }, [account]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(() => {
    account.signIn();
  }, [account]);

  const signOut = useCallback(async () => {
    await account.signOut();
    await refresh();
  }, [account, refresh]);

  return { ...state, refresh, signIn, signOut };
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : '账号服务暂不可用';
}
